import type { ValidComponent } from 'solid-js'
import { createContext, createMemo, createSignal, Show } from 'solid-js'
import { Dynamic } from 'solid-js/web'
import { twMerge } from 'tailwind-merge'
import type { ListContextType, ListPropsType, ValuesGroupType, ValuesMapType } from '~/types/List'

export const ListContext = createContext<ListContextType>({
  dense: false,
  disablePadding: false
})

export default function List(props: ListPropsType) {
  const Component = props.component || 'ul'

  const [internalValuesMap, setInternalValuesMap] = createSignal<ValuesMapType>({})
  const [internalValuesGroups, setInternalValuesGroups] = createSignal<ValuesGroupType>({})

  const getGroupedValues = (): ValuesGroupType => {
    const valuesMap = internalValuesMap()
    const groups: ValuesGroupType = { ...internalValuesGroups() }

    const ungroupedValues: string[] = []
    for (const [value, checked] of Object.entries(valuesMap)) {
      if (checked) {
        ungroupedValues.push(value)
      }
    }

    if (ungroupedValues.length > 0) {
      groups[''] = ungroupedValues
    }

    return groups
  }

  const setSelectedValue = (name: string | undefined, value: string, checked: boolean) => {
    if (!name) {
      setInternalValuesMap(prev => ({ ...prev, [value]: checked }))

      const newValues = { ...internalValuesMap(), [value]: checked }
      const selectedArray = Object.entries(newValues)
        .filter(([_, isSelected]) => isSelected)
        .map(([val]) => val)

      if (props.onValuesChange) {
        props.onValuesChange(selectedArray)
      }
    } else {
      setInternalValuesGroups(prev => {
        const newGroups = { ...prev }

        if (!newGroups[name]) {
          newGroups[name] = []
        }

        if (checked) {
          if (!newGroups[name].includes(value)) {
            newGroups[name] = [...newGroups[name], value]
          }
        } else {
          newGroups[name] = newGroups[name].filter(v => v !== value)
          if (newGroups[name].length === 0) {
            delete newGroups[name]
          }
        }

        return newGroups
      })

      if (props.onValuesChange) {
        props.onValuesChange(getGroupedValues())
      }
    }
  }

  const contextValue = createMemo(() => ({
    dense: props.dense || false,
    disablePadding: props.disablePadding || false,
    setSelectedValue
  }))

  const baseClasses = createMemo(() =>
    twMerge('flex flex-col w-full', 'text-foreground', !props.disablePadding && 'py-1', props.dense ? 'gap-0' : 'gap-0', props.divided && '[&>*:not(:last-child)]:border-b border-border', props.class)
  )

  return (
    <ListContext.Provider value={contextValue()}>
      <Dynamic component={Component as ValidComponent} class={baseClasses()} classList={props.classList} role="list">
        <Show when={props.subheader}>
          <div class="px-4 py-2 text-sm font-medium text-foreground-subtle">{props.subheader}</div>
        </Show>
        {props.children}
      </Dynamic>
    </ListContext.Provider>
  )
}
